import { getConfig } from '../lib/runtime-config';
import { api, ApiError } from './api.service';

export interface SessionUser {
  userId: number;
  email: string;
  mfaEnabled: boolean;
}

export interface LoginResult {
  status: 'authenticated' | 'mfa-required' | 'mfa-setup-required' | 'permission-denied';
  token?: string;
  user?: SessionUser;
  /** Permission strings missing on the user (only set when status is 'permission-denied'). */
  missingPermissions?: string[];
}

type PermissionMap = { [permission: string]: { [scope: string]: boolean } };

interface LoginResponse {
  success: boolean;
  token?: string;
  userId?: number;
  user?: { userId: number; email: string; mfaEnabled?: boolean; mfa_enabled?: number };
  permissions?: PermissionMap;
  tenantId?: number;
  validUntil?: string;
  requiresMfa?: boolean;
  mfaType?: string;
}

/**
 * Permissions a tkw mobile user must have on the active tenant to use the app.
 * UMS_LOGIN is enforced server-side as well, but we list it here so a missing
 * permission is reported explicitly instead of as a generic 403.
 */
const REQUIRED_PERMISSIONS = ['UMS_LOGIN', 'MYMEDAX_TKW_CREATE', 'MYMEDAX_TKW_READ'] as const;

/**
 * Checks if a user has the given permission on a specific tenant scope.
 * The permission map uses '*' as a wildcard for "all tenants".
 */
function hasPermission(permissions: PermissionMap | undefined, name: string, tenantId: number): boolean {
  if (!permissions || !permissions[name]) {
    return false;
  }
  const scopes = permissions[name];
  return scopes['*'] === true || scopes[String(tenantId)] === true;
}

/**
 * Returns the list of REQUIRED_PERMISSIONS the user is missing for the given tenant.
 */
function findMissingPermissions(permissions: PermissionMap | undefined, tenantId: number): string[] {
  return REQUIRED_PERMISSIONS.filter((p) => !hasPermission(permissions, p, tenantId));
}

const TOKEN_KEY = 'tkw-mobile.token';
const USER_KEY = 'tkw-mobile.user';

class AuthStore {
  /**
   * Returns the persisted session token (or null when unauthenticated).
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Returns the cached session user (or null).
   */
  getUser(): SessionUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return null;
    }
    try { return JSON.parse(raw) as SessionUser; } catch { return null; }
  }

  /**
   * Persists the session token and user.
   */
  setSession(token: string, user: SessionUser): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Updates only the user payload, preserving the token.
   */
  patchUser(patch: Partial<SessionUser>): void {
    const current = this.getUser();
    if (!current) {
      return;
    }
    localStorage.setItem(USER_KEY, JSON.stringify({...current, ...patch}));
  }

  /**
   * Clears the persisted session.
   */
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const authStore = new AuthStore();

/**
 * Performs a login against the tkw-api. Handles the three-state response:
 *
 *   - 200 + mfaEnabled=true   → authenticated
 *   - 200 + mfaEnabled=false  → mfa-setup-required (frontend-enforced)
 *   - 428 + requiresMfa=true  → mfa-required (caller must re-call with code)
 */
export async function login(
  username: string,
  password: string,
  code?: string
): Promise<LoginResult> {
  const cfg = getConfig();
  const body: Record<string, unknown> = {
    username,
    password,
    tenantId: cfg.tenantId
  };
  if (code) {
    body.code = code;
  }

  try {
    const response = await api<LoginResponse>('/api/module/login/auth', {
      method: 'PUT',
      body,
      anonymous: true
    });

    if (!response.success || !response.token || !response.user || !response.userId) {
      throw new Error('invalid login response');
    }

    // we only get the permission map once the auth flow is fully complete
    // (no permissions on the 428 mfa-required response). bail out before
    // persisting the session so the user can never access protected pages.
    const tenantId = response.tenantId || cfg.tenantId;
    const missingPermissions = findMissingPermissions(response.permissions, tenantId);
    if (missingPermissions.length > 0) {
      authStore.clear();
      return { status: 'permission-denied', missingPermissions };
    }

    const mfaEnabled = !!(response.user.mfaEnabled || response.user.mfa_enabled);
    const sessionUser: SessionUser = {
      userId: response.userId,
      email: response.user.email,
      mfaEnabled
    };

    authStore.setSession(response.token, sessionUser);

    return {
      status: mfaEnabled ? 'authenticated' : 'mfa-setup-required',
      token: response.token,
      user: sessionUser
    };
  } catch (error) {
    const apiError = error as ApiError;
    if (apiError.status === 428) {
      const body = apiError.body as LoginResponse | null;
      if (body && body.requiresMfa) {
        return { status: 'mfa-required' };
      }
    }
    throw error;
  }
}

/**
 * Logs the user out. Best-effort: clears the local session even when the
 * backend call fails (e.g. expired token).
 */
export async function logout(): Promise<void> {
  const token = authStore.getToken();
  authStore.clear();
  if (!token) {
    return;
  }
  try {
    await api('/api/module/login/auth', { method: 'DELETE' });
  } catch (error) {
    console.warn('logout backend call failed', error);
  }
}

/**
 * Pings the heartbeat endpoint to keep the session warm. Returns false on failure.
 */
export async function heartbeat(): Promise<boolean> {
  try {
    await api('/api/module/login/heartbeat');
    return true;
  } catch {
    return false;
  }
}
